<template>
    <b-modal :id="modalId" :title="modalTitle" size="lg" scrollable>
        <div class="dashboard-customize">
            <div class="mb-2">
                <p class="mb-1">
                    {{ instructionText }}
                </p>
                <b-alert v-if="mode === 'user'" :show="true" class="p-2" style="line-height: 1.5;">
                    <strong>Tip:</strong> use the "All Cards" button on the dashboard to temporarily view all cards,
                    even if they are hidden by default.
                </b-alert>
                <b-alert v-if="mode === 'tenant'" :show="true" variant="info" class="p-2" style="line-height: 1.5;">
                    <strong>Note:</strong> Cards marked as "Hidden for all users" will not be visible to any user,
                    regardless of their personal dashboard settings.
                </b-alert>
            </div>
            <hr>
            </hr>

            <b-row>
                <!-- Column 1 -->
                <b-col cols="6">
                    <div class="column-header">
                        <h6 class="mb-3">
                            <i class="fas fa-arrow-left text-muted mr-2"></i>
                            Left Column
                        </h6>
                    </div>

                    <!-- Fixed item: Patient Actions -->
                    <div class="card-item card-fixed mb-2">
                        <div class="card-content">
                            <div class="card-row">
                                <div class="card-switch invisible">
                                    <!-- Empty space for alignment -->
                                </div>
                                <div class="card-title flex-fill mb-0">
                                    <i class="fas fa-lock mr-2"></i>
                                    Patient Actions <small>(Always visible)</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <draggable v-model="column1Cards" :options="dragOptions" class="card-list" @change="onCardMove">
                        <transition-group name="card-transition" tag="div">
                            <div v-for="card in column1Cards" :key="card.id" class="card-item"
                                :class="{ 'card-hidden': !card.visible, 'card-tenant-hidden': card.tenantHidden }">

                                <div class="card-content">
                                    <!-- User mode: switch and title on same row -->
                                    <div v-if="mode === 'user'" class="card-row">
                                        <div class="card-switch">
                                            <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch>
                                            </b-form-checkbox>
                                        </div>
                                        <div class="card-title flex-fill mb-0">
                                            {{ card.title }}
                                        </div>
                                    </div>

                                    <!-- Tenant mode: title row, then labeled switches -->
                                    <template v-if="mode === 'tenant'">
                                        <div class="card-title mb-0" :class="{ 'text-decoration-line-through': card.tenantHidden }">
                                            {{ card.title }}
                                        </div>
                                        <div class="card-switches-column">
                                            <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch
                                                :disabled="card.tenantHidden">
                                                <span class="text-muted">Visible by default</span>
                                            </b-form-checkbox>
                                            <b-form-checkbox v-model="card.tenantHidden" @change="onTenantHiddenChange(card)" switch>
                                                <span class="text-muted">Hidden for all users</span>
                                            </b-form-checkbox>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </transition-group>
                    </draggable>
                </b-col>

                <!-- Column 2 -->
                <b-col cols="6">
                    <div class="column-header">
                        <h6 class="mb-3">
                            <i class="fas fa-arrow-right text-muted mr-2"></i>
                            Right Column
                        </h6>
                    </div>

                    <!-- Fixed item: Alerts -->
                    <div class="card-item card-fixed mb-2">
                        <div class="card-content">
                            <div class="card-row">
                                <div class="card-switch invisible">
                                    <!-- Empty space for alignment -->
                                </div>
                                <div class="card-title flex-fill mb-0">
                                    <i class="fas fa-lock mr-2"></i>
                                    Alerts <small>(Always visible)</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <draggable v-model="column2Cards" :options="dragOptions" class="card-list" @change="onCardMove">
                        <transition-group name="card-transition" tag="div">
                            <div v-for="card in column2Cards" :key="card.id" class="card-item"
                                :class="{ 'card-hidden': !card.visible, 'card-tenant-hidden': card.tenantHidden }">

                                <div class="card-content">
                                    <!-- User mode: switch and title on same row -->
                                    <div v-if="mode === 'user'" class="card-row">
                                        <div class="card-switch">
                                            <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch>
                                            </b-form-checkbox>
                                        </div>
                                        <div class="card-title flex-fill mb-0">
                                            {{ card.title }}
                                        </div>
                                    </div>

                                    <!-- Tenant mode: title row, then labeled switches -->
                                    <template v-if="mode === 'tenant'">
                                        <div class="card-title mb-0" :class="{ 'text-decoration-line-through': card.tenantHidden }">
                                            {{ card.title }}
                                        </div>
                                        <div class="card-switches-column">
                                            <b-form-checkbox v-model="card.visible" @change="onVisibilityChange" switch
                                                :disabled="card.tenantHidden">
                                                <span class="text-muted">Visible by default</span>
                                            </b-form-checkbox>
                                            <b-form-checkbox v-model="card.tenantHidden" @change="onTenantHiddenChange(card)" switch>
                                                <span class="text-muted">Hidden for all users</span>
                                            </b-form-checkbox>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </transition-group>
                    </draggable>
                </b-col>
            </b-row>
        </div>

        <template #modal-footer>
            <div class="w-100 d-flex justify-content-between pt-2">
                <b-button v-if="showResetButton" variant="danger" size="sm" @click="resetToDefaults">
                    <i class="fas fa-undo mr-1"></i>
                    Reset to Defaults
                </b-button>
                <div v-else></div>
                <div>
                    <b-button variant="secondary" class="mr-2" @click="cancel">
                        Cancel
                    </b-button>
                    <b-button variant="primary" @click="save" :disabled="saving" class="px-4">
                        <b-spinner v-if="saving" small class="mr-1" />
                        <strong>{{ saving ? 'Saving...' : 'Save' }}</strong>
                    </b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import draggable from "vuedraggable";
import { toRaw } from 'vue';
import { DashboardCardConfig, DashboardSettings, DEFAULT_DASHBOARD_CARDS } from "@/models/DashboardSettings";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { getUserSetting, deleteUserSetting } from "@/services/UserSettingsService";

export interface DashboardCustomizeSaveEvent {
    cards: DashboardCardConfig[];
    hiddenCards: string[];
}

@Component({
    components: {
        draggable,
    },
})
export default class DashboardCustomizeModal extends Vue {
    @Prop({ default: 'dashboardCustomizeModal' }) modalId!: string;
    @Prop({ default: 'user' }) mode!: 'user' | 'tenant';
    @Prop({ default: () => [] }) cards!: DashboardCardConfig[];
    @Prop({ default: () => [] }) tenantHiddenCards!: string[];

    private localCards: DashboardCardConfig[] = [];
    private saving = false;

    private dragOptions = {
        animation: 150,
        ghostClass: 'card-ghost',
        chosenClass: 'card-chosen',
        dragClass: 'card-drag',
    };

    get modalTitle(): string {
        return this.mode === 'tenant' ? 'Configure Dashboard Layout' : 'Customize Dashboard';
    }

    get instructionText(): string {
        if (this.mode === 'tenant') {
            return 'Configure the default dashboard layout for all users. Drag and drop cards to reorder them. Toggle visibility to set default card visibility. Check "Hidden for all" to prevent users from seeing specific cards.';
        }
        return 'Drag and drop cards to reorder them. Unswitch a card to hide it from the dashboard by default.';
    }

    get showResetButton(): boolean {
        if (this.mode === 'tenant') {
            return false;
        }
        return !!(this.$root.$data.userSettings['dashboard-layout'] ||
            getUserSetting('dashboard-layout'));
    }

    get column1Cards(): DashboardCardConfig[] {
        return this.localCards
            .filter(card => card.column === 1)
            .sort((a, b) => a.order - b.order);
    }

    set column1Cards(value: DashboardCardConfig[]) {
        value.forEach((card, index) => {
            card.column = 1;
            card.order = index + 1;
        });

        const column2Cards = this.localCards.filter(card => card.column === 2);
        this.localCards = [...value, ...column2Cards];
        this.updateCardsArray();
    }

    get column2Cards(): DashboardCardConfig[] {
        return this.localCards
            .filter(card => card.column === 2)
            .sort((a, b) => a.order - b.order);
    }

    set column2Cards(value: DashboardCardConfig[]) {
        value.forEach((card, index) => {
            card.column = 2;
            card.order = index + 1;
        });

        const column1Cards = this.localCards.filter(card => card.column === 1);
        this.localCards = [...column1Cards, ...value];
        this.updateCardsArray();
    }

    get hasCustomizations(): boolean {
        return !!(this.$root.$data.userSettings['dashboard-layout'] ||
            getUserSetting('dashboard-layout'));
    }

    mounted() {
        this.initializeLocalState();
        this.$root.$on('bv::modal::show', (_: any, modalId: string) => {
            if (modalId === this.modalId) {
                this.initializeLocalState();
            }
        });
    }

    beforeDestroy() {
        this.$root.$off('bv::modal::show');
    }

    @Watch('cards', { deep: true })
    onCardsChanged() {
        this.initializeLocalState();
    }

    private initializeLocalState() {
        this.localCards = structuredClone(this.cards);

        if (this.mode === 'tenant') {
            this.localCards.forEach(card => {
                card.tenantHidden = this.tenantHiddenCards.includes(card.id);
                if (card.tenantHidden) {
                    card.visible = false;
                }
            });
        }
    }

    private updateCardsArray() {
        this.$forceUpdate();
        this.$emit('cards-updated', this.localCards);
    }

    private onCardMove() {
        this.updateCardsArray();
    }

    private onVisibilityChange() {
        this.updateCardsArray();
    }

    private onTenantHiddenChange(card: DashboardCardConfig) {
        if (card.tenantHidden) {
            card.visible = false;
        }
        this.updateCardsArray();
    }

    private resetToDefaults() {
        this.$bvModal.msgBoxConfirm('Are you sure you want to reset all cards to their default positions and visibility?', {
            title: 'Reset to Defaults',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Reset',
            cancelTitle: 'Cancel',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
        }).then((confirmed: boolean) => {
            if (confirmed) {
                deleteUserSetting('dashboard-layout');

                this.localCards = structuredClone(DEFAULT_DASHBOARD_CARDS);
                this.updateCardsArray();

                this.$emit('settings-reset');

                this.$bvModal.hide(this.modalId);
                this.$notification(NotificationOptions.success('Dashboard reset to default settings'));
            }
        });
    }

    private cancel() {
        this.$bvModal.hide(this.modalId);
    }

    private async save() {
        this.saving = true;
        try {
            const cardsClone = structuredClone(toRaw(this.localCards));

            if (this.mode === 'tenant') {
                const hiddenCards = cardsClone
                    .filter((card: DashboardCardConfig) => card.tenantHidden)
                    .map((card: DashboardCardConfig) => card.id);

                cardsClone.forEach((card: DashboardCardConfig) => {
                    delete card.tenantHidden;
                });

                const saveEvent: DashboardCustomizeSaveEvent = {
                    cards: cardsClone,
                    hiddenCards: hiddenCards
                };
                this.$emit('save-settings', saveEvent);
            } else {
                const settings: DashboardSettings = {
                    cards: cardsClone
                };
                this.$emit('save-settings', settings);
            }

            this.$bvModal.hide(this.modalId);

            if (this.mode === 'user') {
                this.$notification(NotificationOptions.success('Dashboard customization saved successfully'));
            }
        } catch (error) {
            console.error('Error saving dashboard settings:', error);
            this.$notification(NotificationOptions.error('Failed to save dashboard settings'));
        } finally {
            this.saving = false;
        }
    }
}
</script>

<style scoped>
.dashboard-customize {
    min-height: 400px;
}

.card-list {
    min-height: 300px;
    border: 2px dashed #dee2e6;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
}

.card-item {
    display: flex;
    align-items: stretch;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    cursor: grab;
    transition: all 0.2s ease;
}

.card-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 0.5rem;
}

.card-row {
    display: flex;
    align-items: center;
}

.card-switches-column {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.25rem;
}

.card-item:active {
    cursor: grabbing;
}

.card-item:hover {
    border-color: #007bff;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.card-item.card-hidden {
    opacity: 0.8;
    background-color: #f8f9fa;
}

.card-item.card-tenant-hidden {
    opacity: 0.6;
    background-color: #fff3cd;
    border-color: #ffc107;
}

.card-switch {
    margin-right: 0.75rem;
    cursor: pointer;
}

.text-decoration-line-through {
    text-decoration: line-through;
}

.card-title {
    font-weight: 500;
}

/* Fixed items styling */
.card-item.card-fixed {
    opacity: 0.6;
    background-color: #f8f9fa;
    cursor: not-allowed;
    border-style: dashed;
    border-color: #ced4da;
}


/* Drag and drop animations */
.card-ghost {
    opacity: 0;
    background: #0250a3 !important;
    color: white;
}

.card-chosen {
    opacity: 1;
    transform: scale(1.02);
}

.card-drag {
    transform: rotate(5deg);
    transition: none;
}

/* Transition animations */
.card-transition-move {
    transition: transform 0.3s;
}

.card-transition-enter-active,
.card-transition-leave-active {
    transition: all 0.3s;
}

.card-transition-enter,
.card-transition-leave-to {
    opacity: 0;
    transform: translateX(30px);
}


/* Responsive adjustments */
@media (max-width: 768px) {
    .card-list {
        padding: 0.5rem;
    }

    .card-item {
        padding: 0.5rem;
        font-size: 0.875rem;
    }
}
</style>
